import React, { useEffect, useState } from "react";
import "./MasterDesignConfigure.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../../../loading/Loading";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import DownloadIcon from "../../../../components/icons/DownloadIcon";
import { getImageUrl } from "../../../../utils/helpers/common";
import PreviewCircleIcon from "../../../../components/icons/PreviewCircleIcon";
import { TemplatesApi } from "../../../../utils/api/Templates";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import EditIcon from "../../../../components/icons/EditIcon";
import { clientRoutes } from "../../../../utils/routes";
import SelectDesignModal from "./SelectDesignModal";

const STEP_MAPPING: any = {
  property_marketing_for_sale: [
    "coming_soon_property_marketing_for_sale",
    "for_sale_listing_property_marketing_for_sale",
    "auction_property_marketing_for_sale",
    "under_offer_property_marketing_for_sale",
    "open_home_property_marketing_for_sale",
  ],
  property_marketing_for_rent: [
    "coming_soon_property_marketing_for_rent",
    "for_rent_listing_property_marketing_for_rent",
    "open_home_property_marketing_for_rent",
    "deposit_taken_property_marketing_for_rent",
  ],
  agent_marketing_for_sale: [
    "sold_agent_marketing_for_sale",
    "review_post_agent_marketing_for_sale",
    "review_recap_agent_marketing_for_sale",
    "under_offer_recap_agent_marketing_for_sale",
    "sold_recap_agent_marketing_for_sale",
  ],
  agent_marketing_for_rent: [
    "leased_agent_marketing_for_rent",
    "review_post_agent_marketing_for_rent",
    "review_recap_agent_marketing_for_rent",
    "leased_recap_agent_marketing_for_rent",
  ],
};

const STEP_LABELS: any = {
  property_marketing_for_sale: "Property marketing for sale",
  property_marketing_for_rent: "Property marketing for rent",
  agent_marketing_for_sale: "Agent marketing for sale",
  agent_marketing_for_rent: "Agent marketing for rent",
};

const chunkArray = (arr: any[], size: number) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

const MasterDesignConfigure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStageData, setSelectedStageData] = useState<{
    label: string;
    stageSlug: string;
    post_type: string;
  } | null>(null);

  const [fullConfig, setFullConfig] = useState<any>(null);

  const fetchData = async () => {
    setLoading(true);
    const res = await TemplatesApi.getDesignTemplateConfigsById({
      design_template_id: id!,
    });

    if (res?.data?.success) {
      setFullConfig(res.data.data?.[0]);
      setData(res.data.data?.[0]?.design_record_id);
    }
    setLoading(false);
  };

  const handleEditClick = (stageLabel: string, stageSlug: string) => {
    setSelectedStageData({
      label: stageLabel,
      stageSlug: stageSlug,
      post_type: fullConfig?.post_type || "image_story_post",
    });
    setIsModalOpen(true);
  };

  const [activeStep, setActiveStep] = useState("property_marketing_for_sale");

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const getStageDesign = (stageKey: string) => {
    const stageOverride = fullConfig?.updated_marketing_stages?.[stageKey];

    if (stageOverride?.design_record_id?._id) {
      return stageOverride.design_record_id;
    }

    return data;
  };

  const getImage = (
    stageKey: string,
    type: "image" | "story",
    designType: "agent" | "agency",
  ) => {
    const stage = data?.[stageKey];
    const design = getStageDesign(stageKey);

    if (!design) return null;

    if (stage?.use_master_design) {
      return type === "image"
        ? design?.image_post_master_design_svg_file?.original_file_path
        : design?.story_post_master_design_svg_file?.original_file_path;
    }

    if (designType === "agent") {
      return type === "image"
        ? design?.[stageKey]?.agent_image_post_master_design_svg_file
            ?.original_file_path
        : design?.[stageKey]?.agent_story_post_master_design_svg_file
            ?.original_file_path;
    }

    if (designType === "agency") {
      return type === "image"
        ? design?.[stageKey]?.agency_image_post_master_design_svg_file
            ?.original_file_path
        : design?.[stageKey]?.agency_story_post_master_design_svg_file
            ?.original_file_path;
    }

    return null;
  };

  if (loading) {
    return (
      <div className="loader-center">
        <Loading />
      </div>
    );
  }

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="master-design-configure-container">
          <div
            className="propertie-view-back-btn"
            onClick={() =>
              navigate(`${clientRoutes.designTemplateTypes}?type=${type}`)
            }
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>

            <p className="bakc-text-contain">Back</p>
          </div>

          <p className="page-title">Master Design Configuration</p>

          <p className="page-sub-title">
            Manage designs across all marketing stages for the master design
          </p>

          <div className="step-tabs">
            {Object.keys(STEP_MAPPING).map((stepKey) => (
              <div
                key={stepKey}
                className={`tab ${activeStep === stepKey ? "active" : ""}`}
                onClick={() => setActiveStep(stepKey)}
              >
                {STEP_LABELS[stepKey]}
              </div>
            ))}
          </div>

          <div className="step-section">
            <div className="step-title">
              Design setup - {STEP_LABELS[activeStep]} stages
            </div>

            <p className="step-subtitle">
              Review or edit your marketing designs
            </p>

            {chunkArray(STEP_MAPPING[activeStep], 2).map(
              (row: any[], rowIndex) => (
                <div className="stage-row" key={rowIndex}>
                  {row.map((stageKey: string) => {
                    const stage = data?.[stageKey];
                    if (!stage) return null;

                    const isOpenHome =
                      stageKey === "open_home_property_marketing_for_sale" ||
                      stageKey === "open_home_property_marketing_for_rent";

                    return (
                      <div
                        className={`stage-block ${isOpenHome ? "full-width-stage" : ""}`}
                        key={stageKey}
                      >
                        <div className="stage-header">
                          <img src="/images/image-placeholder.svg" alt="" />
                          <span>{stage?.stage_label}</span>
                        </div>

                        <div className="designs-split-container">
                          <div className="template-card-wrapper">
                            <div className="template-header-bar">
                              <div className="header-left">
                                <p className="template-name">Agent designs</p>
                                <p className="template-description">
                                  Verify or customize image and story designs
                                  for agent posts
                                </p>
                              </div>
                              <div
                                className="edit-icon-wrapper"
                                onClick={() =>
                                  handleEditClick(stage?.stage_label, stageKey)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                <EditIcon />
                              </div>
                            </div>

                            <div className="template-preview-row">
                              <div className="post-column">
                                <div className="image-post-box">
                                  <img
                                    src={
                                      getImage(stageKey, "image", "agent")
                                        ? getImageUrl(
                                            getImage(
                                              stageKey,
                                              "image",
                                              "agent",
                                            ),
                                          )
                                        : "/images/image-placeholder.svg"
                                    }
                                    alt="Image Design"
                                  />
                                </div>
                                <div className="post-footer">
                                  <span>Image Design</span>
                                  <div className="footer-icons">
                                    <DownloadIcon />
                                    <PreviewCircleIcon />
                                  </div>
                                </div>
                              </div>

                              <div className="post-column">
                                <div className="story-post-box">
                                  <div className="story-inner-img">
                                    <img
                                      src={
                                        getImage(stageKey, "story", "agent")
                                          ? getImageUrl(
                                              getImage(
                                                stageKey,
                                                "story",
                                                "agent",
                                              ),
                                            )
                                          : "/images/image-placeholder.svg"
                                      }
                                      alt="Story Design"
                                    />
                                  </div>
                                </div>
                                <div className="post-footer">
                                  <span>Story Design</span>
                                  <div className="footer-icons">
                                    <DownloadIcon />
                                    <PreviewCircleIcon />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {isOpenHome && (
                            <div className="template-card-wrapper">
                              <div className="template-header-bar">
                                <div className="header-left">
                                  <p className="template-name">
                                    Agency designs
                                  </p>
                                  <p className="template-description">
                                    Verify or customize image and story designs
                                    for agency posts
                                  </p>
                                </div>
                                <div
                                  className="edit-icon-wrapper"
                                  onClick={() =>
                                    handleEditClick(
                                      stage?.stage_label,
                                      stageKey,
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <EditIcon />
                                </div>
                              </div>

                              <div className="template-preview-row">
                                <div className="post-column">
                                  <div className="image-post-box">
                                    <img
                                      src={
                                        getImage(stageKey, "image", "agency")
                                          ? getImageUrl(
                                              getImage(
                                                stageKey,
                                                "image",
                                                "agency",
                                              ),
                                            )
                                          : "/images/image-placeholder.svg"
                                      }
                                      alt="Image Design"
                                    />
                                  </div>
                                  <div className="post-footer">
                                    <span>Image Design</span>
                                    <div className="footer-icons">
                                      <DownloadIcon />
                                      <PreviewCircleIcon />
                                    </div>
                                  </div>
                                </div>

                                <div className="post-column">
                                  <div className="story-post-box">
                                    <div className="story-inner-img">
                                      <img
                                        src={
                                          getImage(stageKey, "story", "agency")
                                            ? getImageUrl(
                                                getImage(
                                                  stageKey,
                                                  "story",
                                                  "agency",
                                                ),
                                              )
                                            : "/images/image-placeholder.svg"
                                        }
                                        alt="Story Design"
                                      />
                                    </div>
                                  </div>
                                  <div className="post-footer">
                                    <span>Story Design</span>
                                    <div className="footer-icons">
                                      <DownloadIcon />
                                      <PreviewCircleIcon />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            )}
          </div>
        </div>

        <SelectDesignModal
          show={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          stageLabel={selectedStageData?.label || ""}
          selectedDesignTemplateStageSlug={selectedStageData?.stageSlug || ""}
          selectedDesignTemplateId={fullConfig?.design_record_id?._id || ""}
          postType={selectedStageData?.post_type || ""}
        />
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default MasterDesignConfigure;
